import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface Customer {
  id: string;
  name: string;
  description: string | null;
  industry: string | null;
  region: 'EMEA' | 'Americas' | 'APAC' | null;
  country: string | null;
  website: string | null;
  accountManager: string | null;
  accountManagerId: string | null;
  technicalLead: string | null;
  technicalLeadId: string | null;
  tier: 'standard' | 'accelerated' | 'enterprise' | null;
  logoUrl?: string | null;
  notes: string | null;
  status: 'prospect' | 'active' | 'inactive';
  projectsCount: number;
  quickWinsCount: number;
  keyProjectsCount: number;
  adoptionScore: number;
  createdAt: string;
  updatedAt: string;
}

export type RagStatus = 'green' | 'amber' | 'red' | 'na';

export interface CustomerOverview {
  pipeline: {
    totalValue: number;
    pipelineValue: number;
    scopedValue: number;
    unscopedValue: number;
    deliveredValue: number;
    opportunityCount: number;
    pipelineOpportunityCount: number;
    deliveryOpportunityCount: number;
  };
  projects: {
    total: number;
    active: number;
    inDelivery: number;
  };
  health: {
    overall: RagStatus;
    customerRelationship: RagStatus;
    scoping: RagStatus;
    readiness: RagStatus;
    delivery: RagStatus;
    projects: RagStatus;
  };
  adoption: {
    totalComponents: number;
    adoptedCount: number;
    scorePercent: number;
    counts: {
      notYet: number;
      discussion: number;
      poc: number;
      certified: number;
      inUse: number;
      deferred: number;
      decomm: number;
    };
  };
  tarPlan: {
    id: string;
    name: string;
    updatedAt: string;
  } | null;
  activeOpportunities: Array<{
    id: string;
    title: string;
    currentScopingStage: string | null;
    servicesValue: number;
    scopedValue: number;
    currency: string;
    customerRelationshipStatus: RagStatus;
  }>;
  activeProjects: Array<{
    id: string;
    name: string;
    status: string;
    currentScopingStage: string | null;
    healthStatus: RagStatus;
    totalCost: number;
    totalAdjustedDays: number;
    currency: string;
    opportunityTitle: string | null;
  }>;
}

export interface CustomerNote {
  id: string;
  customerId: string;
  content: string;
  type: 'call' | 'meeting' | 'email' | 'note';
  createdBy: string | null;
  createdAt: string;
}

export interface CustomerEngagement {
  id: string;
  customerId: string;
  name: string;
  type: 'key_project' | 'quick_win';
  status: 'planned' | 'in_progress' | 'complete';
  description: string | null;
  componentId: string | null;
  effort: 'low' | 'medium' | 'high' | null;
  value: 'low' | 'medium' | 'high' | null;
  targetDate: string | null;
  linkedProjectId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type AdoptionStatus = 'not_yet' | 'discussion' | 'poc' | 'certified' | 'in_use' | 'deferred' | 'decomm';

export interface CustomerAdoption {
  id: string;
  customerId: string;
  componentId: string;
  adoptionStatus: AdoptionStatus;
  year: number;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VcfComponent {
  id: string;
  code: string;
  name: string;
}

export interface CustomerAdoptionVersion {
  id: string;
  customerId: string;
  version: number;
  snapshot: Array<{
    componentId: string;
    adoptionStatus: AdoptionStatus;
    year: number;
    notes: string | null;
  }>;
  changeDescription: string | null;
  createdBy: string | null;
  createdAt: string;
}

interface CustomersResponse {
  customers: Customer[];
  total: number;
  limit: number;
  offset: number;
}

interface CustomerResponse {
  customer: Customer;
}

interface CreateCustomerInput {
  name: string;
  industry?: string;
  region?: 'EMEA' | 'Americas' | 'APAC';
  country?: string;
  website?: string;
  accountManager?: string | null;
  accountManagerId?: string | null;
  technicalLead?: string | null;
  technicalLeadId?: string | null;
  tier?: 'standard' | 'accelerated' | 'enterprise';
  notes?: string;
  status?: 'prospect' | 'active' | 'inactive';
}

interface UpdateCustomerInput extends Partial<CreateCustomerInput> {}

// ============ Hooks ============

export function useCustomers(params?: {
  search?: string;
  status?: Customer['status'];
  region?: 'EMEA' | 'Americas' | 'APAC';
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['customers', params],
    queryFn: () => api.get<CustomersResponse>('/customers', params as Record<string, string | number | boolean | undefined>),
  });
}

export function useCustomer(id: string) {
  return useQuery({
    queryKey: ['customers', id],
    queryFn: () => api.get<CustomerResponse>(`/customers/${id}`),
    enabled: !!id,
  });
}

export function useCustomerOverview(id: string) {
  return useQuery({
    queryKey: ['customers', id, 'overview'],
    queryFn: () => api.get<CustomerOverview>(`/customers/${id}/overview`),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCustomerInput) =>
      api.post<{ customer: Customer }>('/customers', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateCustomerInput) =>
      api.patch<{ customer: Customer }>(`/customers/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers', id] });
    },
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/customers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
}

export function useCustomerNotes(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'notes'],
    queryFn: () => api.get<{ notes: CustomerNote[] }>(`/customers/${customerId}/notes`),
    enabled: !!customerId,
  });
}

export function useCreateCustomerNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      content,
      type,
    }: {
      customerId: string;
      content: string;
      type?: CustomerNote['type'];
    }) => api.post<{ note: CustomerNote }>(`/customers/${customerId}/notes`, { content, type }),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'notes'] });
    },
  });
}

export function useCustomerEngagements(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'engagements'],
    queryFn: () => api.get<{ engagements: CustomerEngagement[] }>(`/customers/${customerId}/engagements`),
    enabled: !!customerId,
  });
}

export function useCreateCustomerEngagement() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      ...data
    }: {
      customerId: string;
      name: string;
      type: CustomerEngagement['type'];
      status?: CustomerEngagement['status'];
      description?: string;
      effort?: CustomerEngagement['effort'];
      value?: CustomerEngagement['value'];
      targetDate?: string;
    }) => api.post<{ engagement: CustomerEngagement }>(`/customers/${customerId}/engagements`, data),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'engagements'] });
      queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
    },
  });
}

// ============ Adoption Hooks ============

export function useCustomerAdoptions(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'adoptions'],
    queryFn: () => api.get<{ adoptions: CustomerAdoption[]; components: VcfComponent[] }>(
      `/customers/${customerId}/adoptions`
    ),
    enabled: !!customerId,
  });
}

export function useUpdateCustomerAdoptions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      adoptions,
    }: {
      customerId: string;
      adoptions: Array<{
        componentId: string;
        year: number;
        adoptionStatus: AdoptionStatus;
        notes?: string;
      }>;
    }) => api.put<{ adoptions: CustomerAdoption[] }>(`/customers/${customerId}/adoptions`, { adoptions }),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'adoptions'] });
      queryClient.invalidateQueries({ queryKey: ['customers', customerId] });
    },
  });
}

export function useCreateAdoptionVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      changeDescription,
    }: {
      customerId: string;
      changeDescription?: string;
    }) => api.post<{ version: CustomerAdoptionVersion }>(
      `/customers/${customerId}/adoptions/version`,
      { changeDescription }
    ),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({ queryKey: ['customers', customerId, 'adoptions', 'versions'] });
    },
  });
}

export function useAdoptionVersions(customerId: string) {
  return useQuery({
    queryKey: ['customers', customerId, 'adoptions', 'versions'],
    queryFn: () => api.get<{
      versions: Array<{
        id: string;
        version: number;
        changeDescription: string | null;
        createdBy: string | null;
        createdAt: string;
      }>;
    }>(`/customers/${customerId}/adoptions/versions`),
    enabled: !!customerId,
  });
}

export function useAdoptionVersion(customerId: string, version: number) {
  return useQuery({
    queryKey: ['customers', customerId, 'adoptions', 'versions', version],
    queryFn: () => api.get<{ version: CustomerAdoptionVersion }>(
      `/customers/${customerId}/adoptions/versions/${version}`
    ),
    enabled: !!customerId && version > 0,
  });
}
